/**
 * useMergeRef Polyfill
 *
 * Provides polyfills for ref merging utilities used by Radix UI.
 * React global exposure is handled by react-shim.js.
 */

import React from 'react';

// Define React hooks locally for safe access
const { useRef, useCallback } = React;

// Polyfill for useMergeRef with guaranteed React access
export function useMergeRef(...refs) {
  return useCallback(node => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref && typeof ref === 'object') {
        ref.current = node;
      }
    });
  }, refs);
}

// Alias for useMergeRefs (plural)
export function useMergeRefs(...refs) {
  return useMergeRef(...refs);
}

// Additional functions to match use-callback-ref package API
export function assignRef(ref, value) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref && typeof ref === 'object') {
    ref.current = value;
  }
}

export function mergeRefs(...refs) {
  return (value) => {
    refs.forEach(ref => assignRef(ref, value));
  };
}

export function useCallbackRef(initialValue, callback) {
  const ref = useRef(initialValue);
  const callbackRef = useCallback((newValue) => {
    ref.current = newValue;
    if (callback) callback(newValue);
  }, [callback]);
  return [ref, callbackRef];
}

export function createCallbackRef(callback) {
  return (value) => {
    if (callback) callback(value);
  };
}

export function useTransformRef(ref, transformer) {
  return useCallback((value) => {
    const transformed = transformer ? transformer(value) : value;
    assignRef(ref, transformed);
  }, [ref, transformer]);
}

export function transformRef(ref, transformer) {
  return (value) => {
    const transformed = transformer ? transformer(value) : value;
    assignRef(ref, transformed);
  };
}

// Re-export React hooks to ensure they're available
export {
  useRef,
  useCallback,
  React
};

// Also export as default
export default useMergeRef;